import { defineAsyncComponent } from 'vue';
import { GlAlert, GlBadge, GlButton, GlDisclosureDropdown, GlDisclosureDropdownItem, GlIcon, GlLink, GlModal, GlModalDirective, GlTooltipDirective, GlEmptyState, GlTab, GlTabs, GlSprintf } from '@gitlab/ui';
import { createAlert } from '~/alert';
import { TYPENAME_PACKAGES_PACKAGE } from '~/graphql_shared/constants';
import { convertToGraphQLId } from '~/graphql_shared/utils';
import { objectToQuery } from '~/lib/utils/url_utility';
import { s__, __ } from '~/locale';
import { packageTypeToTrackCategory } from '~/packages_and_registries/package_registry/utils';
import AdditionalMetadata from '~/packages_and_registries/package_registry/components/details/additional_metadata.vue';
import DependencyRow from '~/packages_and_registries/package_registry/components/details/dependency_row.vue';
import InstallationCommands from '~/packages_and_registries/package_registry/components/details/installation_commands.vue';
import PackageHistory from '~/packages_and_registries/package_registry/components/details/package_history.vue';
import PackageTitle from '~/packages_and_registries/package_registry/components/details/package_title.vue';
import DeletePackages from '~/packages_and_registries/package_registry/components/functional/delete_packages.vue';
import { PACKAGE_DEPRECATED_STATUS, PACKAGE_TYPE_NUGET, PACKAGE_TYPE_COMPOSER, PACKAGE_TYPE_CONAN, PACKAGE_TYPE_MAVEN, PACKAGE_TYPE_PYPI, PACKAGE_TYPE_NPM, DELETE_PACKAGE_TRACKING_ACTION, REQUEST_DELETE_PACKAGE_TRACKING_ACTION, CANCEL_DELETE_PACKAGE_TRACKING_ACTION, INSTALL_PACKAGE_TRACKING_ACTION, REQUEST_FORWARDING_HELP_PAGE_PATH, SHOW_DELETE_SUCCESS_ALERT, FETCH_PACKAGE_DETAILS_ERROR_MESSAGE, DELETE_PACKAGE_REQUEST_FORWARDING_MODAL_CONTENT, DELETE_MODAL_TITLE, DELETE_MODAL_CONTENT, GRAPHQL_PAGE_SIZE } from '~/packages_and_registries/package_registry/constants';
import getPackageDetails from '~/packages_and_registries/package_registry/graphql/queries/get_package_details.query.graphql';
import getGroupPackageSettings from '~/packages_and_registries/package_registry/graphql/queries/get_group_package_settings.query.graphql';
import getPackageVersionsQuery from '~/packages_and_registries/package_registry/graphql/queries/get_package_versions.query.graphql';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import Tracking from '~/tracking';
export default {
    name: 'PackagesApp',
    components: {
        GlAlert,
        GlBadge,
        GlButton,
        GlDisclosureDropdown,
        GlDisclosureDropdownItem,
        GlEmptyState,
        GlModal,
        GlIcon,
        GlLink,
        GlTab,
        GlTabs,
        GlSprintf,
        PackageTitle,
        DependencyRow,
        PackageHistory,
        AdditionalMetadata,
        InstallationCommands,
        PackageFiles: defineAsyncComponent(()=>import('~/packages_and_registries/package_registry/components/details/package_files.vue')),
        DeletePackages,
        PackageVersionsList: defineAsyncComponent(()=>import('~/packages_and_registries/package_registry/components/details/package_versions_list.vue'))
    },
    directives: {
        GlTooltip: GlTooltipDirective,
        GlModal: GlModalDirective
    },
    mixins: [
        Tracking.mixin()
    ],
    inject: [
        'emptyListIllustration',
        'projectListUrl',
        'groupListUrl'
    ],
    trackingActions: {
        DELETE_PACKAGE_TRACKING_ACTION,
        REQUEST_DELETE_PACKAGE_TRACKING_ACTION,
        CANCEL_DELETE_PACKAGE_TRACKING_ACTION,
        INSTALL_PACKAGE_TRACKING_ACTION
    },
    data () {
        return {
            deletePackageModalContent: DELETE_MODAL_CONTENT,
            versionsMutationLoading: false,
            packageEntity: {},
            groupSettings: {},
            showInstructions: false,
            isDropdownVisible: false
        };
    },
    apollo: {
        packageEntity: {
            query: getPackageDetails,
            variables () {
                return this.queryVariables;
            },
            update (data) {
                return data.package || {};
            },
            error (error) {
                createAlert({
                    message: FETCH_PACKAGE_DETAILS_ERROR_MESSAGE,
                    captureError: true,
                    error
                });
            }
        },
        groupSettings: {
            query: getGroupPackageSettings,
            variables () {
                return {
                    fullPath: this.projectPath
                };
            },
            update (data) {
                return data.project?.group?.packageSettings || {};
            },
            skip () {
                return !(this.isRequestForwardingSupported && this.canDelete);
            },
            error (error) {
                Sentry.captureException(error);
            }
        }
    },
    computed: {
        canDelete () {
            return this.packageEntity.userPermissions?.destroyPackage;
        },
        deleteModalContent () {
            return this.isRequestForwardingEnabled ? DELETE_PACKAGE_REQUEST_FORWARDING_MODAL_CONTENT : this.deletePackageModalContent;
        },
        projectName () {
            return this.packageEntity.project?.name;
        },
        projectPath () {
            return this.packageEntity.project?.fullPath;
        },
        packageId () {
            return this.$route.params.id;
        },
        queryVariables () {
            return {
                id: convertToGraphQLId(TYPENAME_PACKAGES_PACKAGE, this.packageId)
            };
        },
        packageType () {
            return this.packageEntity.packageType;
        },
        isLoading () {
            return this.$apollo.queries.packageEntity.loading;
        },
        isRequestForwardingSupported () {
            return [
                PACKAGE_TYPE_MAVEN,
                PACKAGE_TYPE_PYPI,
                PACKAGE_TYPE_NPM
            ].includes(this.packageType);
        },
        isRequestForwardingEnabled () {
            return this.isRequestForwardingSupported && this.groupSettings[`${this.packageType.toLowerCase()}PackageRequestsForwarding`];
        },
        isValidPackage () {
            return this.isLoading || Boolean(this.packageEntity.name);
        },
        // eslint-disable-next-line vue/no-unused-properties -- tracking() is required by Tracking mixin.
        tracking () {
            return {
                category: packageTypeToTrackCategory(this.packageType)
            };
        },
        packageDependencies () {
            return this.packageEntity.dependencyLinks?.nodes || [];
        },
        packageVersionsCount () {
            return this.packageEntity.versions?.count ?? 0;
        },
        showDependencies () {
            return this.packageType === PACKAGE_TYPE_NUGET;
        },
        showDeprecationAlert () {
            return this.packageEntity.status === PACKAGE_DEPRECATED_STATUS;
        },
        showFiles () {
            return this.packageType !== PACKAGE_TYPE_COMPOSER;
        },
        showMetadata () {
            return [
                PACKAGE_TYPE_COMPOSER,
                PACKAGE_TYPE_CONAN,
                PACKAGE_TYPE_MAVEN,
                PACKAGE_TYPE_NUGET,
                PACKAGE_TYPE_PYPI
            ].includes(this.packageType);
        },
        refetchVersionsQueryData () {
            return [
                {
                    query: getPackageVersionsQuery,
                    variables: {
                        id: this.queryVariables.id,
                        first: GRAPHQL_PAGE_SIZE
                    }
                }
            ];
        },
        hasInstallationInstructions () {
            const packageTypesWithInstructions = [
                PACKAGE_TYPE_CONAN,
                PACKAGE_TYPE_MAVEN,
                PACKAGE_TYPE_NPM,
                PACKAGE_TYPE_NUGET,
                PACKAGE_TYPE_PYPI
            ];
            // Composer packages only have installation instructions on the group level
            if (this.packageType === PACKAGE_TYPE_COMPOSER) {
                return Boolean(this.groupListUrl);
            }
            // Other package types that support installation instructions
            return packageTypesWithInstructions.includes(this.packageType);
        }
    },
    methods: {
        navigateToListWithSuccessModal () {
            const returnTo = !this.groupListUrl || document.referrer.includes(this.projectName) ? this.projectListUrl : this.groupListUrl; // to avoid security issue url are supplied from backend
            const modalQuery = objectToQuery({
                [SHOW_DELETE_SUCCESS_ALERT]: true
            });
            window.location.replace(`${returnTo}?${modalQuery}`);
        },
        handleAllFilesDelete (content) {
            this.deletePackageModalContent = content;
            this.$refs.deleteModal.show();
        },
        resetDeleteModalContent () {
            this.deletePackageModalContent = DELETE_MODAL_CONTENT;
        },
        toggleShowInstructions () {
            this.showInstructions = !this.showInstructions;
            this.track(this.$options.trackingActions.INSTALL_PACKAGE_TRACKING_ACTION);
        },
        showDropdown () {
            this.isDropdownVisible = true;
        },
        hideDropdown () {
            this.isDropdownVisible = false;
        },
        showDropdownTooltip () {
            return !this.isDropdownVisible ? this.$options.i18n.moreActionsTooltip : '';
        }
    },
    i18n: {
        DELETE_MODAL_TITLE,
        otherVersionsTabTitle: s__('PackageRegistry|Other versions'),
        moreActionsTooltip: __('Actions')
    },
    links: {
        REQUEST_FORWARDING_HELP_PAGE_PATH
    },
    modal: {
        packageDeletePrimaryAction: {
            text: s__('PackageRegistry|Permanently delete'),
            attributes: {
                variant: 'danger',
                category: 'primary',
                'data-testid': 'delete-modal-button'
            }
        },
        fileDeletePrimaryAction: {
            text: __('Delete'),
            attributes: {
                variant: 'danger',
                category: 'primary'
            }
        },
        filesDeletePrimaryAction: {
            text: s__('PackageRegistry|Permanently delete assets'),
            attributes: {
                variant: 'danger',
                category: 'primary'
            }
        },
        cancelAction: {
            text: __('Cancel')
        }
    }
};
